/**
 * Military to Civilian Role Translation Utilities
 * Converts military ranks, MOS codes, and experience into civilian-friendly language
 */

// Rank level mapping for civilian equivalents
const RANK_LEVELS = {
  // Enlisted - Junior
  'E-1': { level: '', category: 'junior', yearsEquiv: 0 },
  'E-2': { level: '', category: 'junior', yearsEquiv: 1 },
  'E-3': { level: '', category: 'junior', yearsEquiv: 2 },
  // Enlisted - NCO
  'E-4': { level: '', category: 'nco', yearsEquiv: 3 },
  'E-5': { level: '', category: 'nco', yearsEquiv: 5 },
  'E-6': { level: 'Senior', category: 'nco', yearsEquiv: 8 },
  // Enlisted - Senior NCO
  'E-7': { level: '', category: 'senior_nco', yearsEquiv: 12 },
  'E-8': { level: 'Senior', category: 'senior_nco', yearsEquiv: 16 },
  'E-9': { level: 'Senior', category: 'senior_nco', yearsEquiv: 20 },
  // Warrant Officers
  'W-1': { level: 'Technical', category: 'warrant', yearsEquiv: 8 },
  'W-2': { level: 'Technical', category: 'warrant', yearsEquiv: 10 },
  'W-3': { level: 'Senior Technical', category: 'warrant', yearsEquiv: 14 },
  'W-4': { level: 'Senior Technical', category: 'warrant', yearsEquiv: 18 },
  'W-5': { level: 'Senior Technical', category: 'warrant', yearsEquiv: 22 },
  // Officers - Company Grade
  'O-1': { level: '', category: 'company_officer', yearsEquiv: 2 },
  'O-2': { level: '', category: 'company_officer', yearsEquiv: 4 },
  'O-3': { level: '', category: 'company_officer', yearsEquiv: 6 },
  // Officers - Field Grade
  'O-4': { level: 'Senior', category: 'field_officer', yearsEquiv: 10 },
  'O-5': { level: 'Senior', category: 'field_officer', yearsEquiv: 16 },
  'O-6': { level: 'Senior', category: 'field_officer', yearsEquiv: 20 },
  // Officers - General/Flag
  'O-7': { level: 'Senior', category: 'general', yearsEquiv: 24 },
  'O-8': { level: 'Senior', category: 'general', yearsEquiv: 28 },
  'O-9': { level: 'Senior', category: 'general', yearsEquiv: 30 },
  'O-10': { level: 'Senior', category: 'general', yearsEquiv: 32 },
};

// MOS/Job category to civilian functional area mapping
const MOS_CATEGORY_MAP = {
  // Combat/Operations
  'infantry': 'Operations & Security',
  'armor': 'Operations & Logistics',
  'artillery': 'Operations & Systems',
  'combat': 'Operations',
  'special operations': 'Strategic Operations',
  'reconnaissance': 'Intelligence & Analysis',
  
  // Support/Logistics
  'logistics': 'Supply Chain & Logistics',
  'supply': 'Supply Chain Management',
  'transportation': 'Transportation & Logistics',
  'maintenance': 'Maintenance & Operations',
  'ordnance': 'Technical Operations',
  
  // Technical
  'signal': 'Information Technology',
  'communications': 'Communications & Technology',
  'cyber': 'Cybersecurity',
  'intelligence': 'Intelligence & Analysis',
  'electronic': 'Electronics & Technology',
  
  // Medical
  'medical': 'Healthcare',
  'dental': 'Healthcare',
  'nurse': 'Healthcare',
  'health': 'Healthcare Administration',
  
  // Administrative
  'administration': 'Administration',
  'personnel': 'Human Resources',
  'finance': 'Finance & Accounting',
  'legal': 'Legal & Compliance',
  'public affairs': 'Communications & Public Relations',
  
  // Engineering
  'engineer': 'Engineering',
  'construction': 'Construction & Engineering',
  
  // Aviation
  'aviation': 'Aviation Operations',
  'pilot': 'Aviation',
  'aircraft': 'Aviation Maintenance',
};

// Leadership scope descriptors
const LEADERSHIP_SCOPE = {
  junior: 'Individual Contributor',
  nco: 'Team Leadership',
  senior_nco: 'Department Leadership',
  warrant: 'Technical Leadership',
  company_officer: 'Division Leadership',
  field_officer: 'Multi-Team Leadership',
  general: 'Organizational Leadership',
};

/**
 * Extract the pay grade from rank string (e.g., "E-7 / Sergeant First Class" -> "E-7")
 */
function extractPayGrade(rank) {
  if (!rank) return null;
  const match = rank.match(/([EWO]-\d+)/i);
  return match ? match[1].toUpperCase() : null;
}

/**
 * Officer / Warrant Officer / Enlisted classification derived from the AUTHORITATIVE rank
 * pay-grade prefix (O-* = Officer, W-* = Warrant Officer, E-* = Enlisted). Returns null when the
 * rank is missing/unparseable so callers can keep a neutral fallback. Never guesses from MOS.
 */
export function getServiceClassification(rank) {
  const pg = extractPayGrade(rank);
  if (!pg) return null;
  const p = pg.charAt(0);
  return p === 'O' ? 'Officer' : p === 'W' ? 'Warrant Officer' : p === 'E' ? 'Enlisted' : null;
}

/**
 * Display-time correction for MOS/AOC titles that carry a stale embedded classification, e.g.
 * "Infantryman (Army - Enlisted)". Rewrites ONLY the classification word after the branch using
 * the member's actual rank, preserving the branch and occupational meaning. If the rank cannot be
 * parsed, the original title is returned unchanged (neutral fallback). No data is mutated.
 */
export function sanitizeMosTitle(mosTitle, rank) {
  if (!mosTitle) return mosTitle;
  const cls = getServiceClassification(rank);
  if (!cls) return mosTitle; // rank unknown → leave as-is
  return mosTitle.replace(/\(([^)]*?)\s-\s[^)]*\)/, (full, branch) => `(${branch.trim()} - ${cls})`);
}

/**
 * Get the rank title without pay grade (e.g., "E-7 / Sergeant First Class" -> "Sergeant First Class")
 */
function extractRankTitle(rank) {
  if (!rank) return null;
  const parts = rank.split(/[\/\-–—]/);
  if (parts.length > 1) {
    return parts[1].trim();
  }
  // If no delimiter, check if it starts with pay grade
  const payGrade = extractPayGrade(rank);
  if (payGrade && rank.startsWith(payGrade)) {
    return rank.substring(payGrade.length).trim().replace(/^[\s\/\-–—]+/, '');
  }
  return rank;
}

/**
 * Determine the functional area from MOS title/code
 */
function determineFunctionalArea(mosTitle, mosCode) {
  const searchText = `${mosTitle || ''} ${mosCode || ''}`.toLowerCase();
  
  for (const [keyword, area] of Object.entries(MOS_CATEGORY_MAP)) {
    if (searchText.includes(keyword)) {
      return area;
    }
  }
  
  return 'Operations & Management';
}

/**
 * Generate civilianized job title from military role
 * Deterministic, credibility-safe: [prefix] [functionalArea] [suffix]
 */
export function getCivilianizedTitle(militaryRole) {
  if (!militaryRole) return 'Professional';
  
  const payGrade = extractPayGrade(militaryRole.rank);
  const rankInfo = payGrade ? RANK_LEVELS[payGrade] : null;
  const functionalArea = determineFunctionalArea(militaryRole.mos_title, militaryRole.mos_code);
  
  if (!rankInfo) {
    return `${functionalArea} Professional`;
  }
  
  const prefix = rankInfo.level; // '', 'Senior', 'Technical', 'Senior Technical'
  const cat = rankInfo.category;
  
  // Leader track: senior NCOs, all officers
  if (cat === 'senior_nco' || cat === 'company_officer' || cat === 'field_officer' || cat === 'general') {
    return prefix ? `${prefix} ${functionalArea} Leader` : `${functionalArea} Leader`;
  }
  
  // Technical track: warrant officers
  if (cat === 'warrant') {
    return `${prefix} ${functionalArea} Specialist`;
  }
  
  // Specialist track: NCOs (E-4 to E-6)
  if (cat === 'nco') {
    return prefix ? `${prefix} ${functionalArea} Specialist` : `${functionalArea} Specialist`;
  }
  
  // Entry track: junior enlisted (E-1 to E-3)
  return `${functionalArea} Associate`;
}

/**
 * Get service context line for closed accordion state
 */
export function getServiceContext(militaryRole, branch) {
  if (!militaryRole && !branch) return '';
  
  const branchName = branch || militaryRole?.branch;
  const payGrade = extractPayGrade(militaryRole?.rank);
  const rankInfo = payGrade ? RANK_LEVELS[payGrade] : null;
  
  let context = '';
  
  if (branchName) {
    context += `Former U.S. ${branchName}`;
  } else {
    context += 'Veteran';
  }
  
  // Add a single descriptor based on level
  if (rankInfo) {
    const scope = LEADERSHIP_SCOPE[rankInfo.category];
    if (scope && scope !== 'Individual Contributor') {
      context += ` · ${scope}`;
    }
  }
  
  return context;
}

/**
 * Get formatted rank with translation for expanded view
 */
export function getFormattedRank(rank) {
  if (!rank) return null;
  
  const payGrade = extractPayGrade(rank);
  const title = extractRankTitle(rank);
  
  if (payGrade && title) {
    return `${payGrade} / ${title}`;
  }
  
  return rank;
}

/**
 * Calculate years of service from military roles
 */
export function calculateYearsOfService(militaryRoles) {
  if (!militaryRoles || militaryRoles.length === 0) return null;
  
  let totalMonths = 0;
  
  for (const role of militaryRoles) {
    if (role.start_date) {
      const start = new Date(role.start_date);
      const end = role.end_date ? new Date(role.end_date) : new Date();
      const months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
      totalMonths += Math.max(0, months);
    }
  }
  
  if (totalMonths === 0) return null;
  
  const years = Math.round(totalMonths / 12);
  return years;
}

/**
 * Get curated core strengths from skills (max 5, outcome-oriented)
 */
export function getCoreStrengths(militaryRoles, maxCount = 5) {
  if (!militaryRoles || militaryRoles.length === 0) return [];
  
  // Priority skills that are outcome-oriented
  const prioritySkills = [
    'Leadership', 'Team Leadership', 'Project Management', 'Operations',
    'Strategic Planning', 'Training', 'Communication', 'Problem Solving',
    'Risk Management', 'Compliance', 'Security', 'Logistics',
    'Technical Operations', 'Analysis', 'Process Improvement'
  ];
  
  // Collect all skills from all roles
  const allSkills = new Set();
  for (const role of militaryRoles) {
    const skills = role.selected_skills || role.skills || [];
    skills.forEach(s => allSkills.add(s));
  }
  
  // Filter and prioritize
  const curated = [];
  
  // First, add priority skills that exist
  for (const priority of prioritySkills) {
    if (allSkills.has(priority) && curated.length < maxCount) {
      curated.push(priority);
    }
  }
  
  // If not enough, add remaining skills
  for (const skill of allSkills) {
    if (!curated.includes(skill) && curated.length < maxCount) {
      // Skip very generic or internal-looking skills
      if (!skill.includes('KSA') && skill.length > 2) {
        curated.push(skill);
      }
    }
  }
  
  return curated.slice(0, maxCount);
}

/**
 * Get translated role summary for expanded view
 */
export function getTranslatedRoleSummary(militaryRole) {
  if (!militaryRole) return null;
  
  const civilianTitle = getCivilianizedTitle(militaryRole);
  const functionalArea = determineFunctionalArea(militaryRole.mos_title, militaryRole.mos_code);
  
  return {
    title: civilianTitle,
    functionalArea,
    summary: militaryRole.ai_summary_text || militaryRole.translated_description || null
  };
}

export default {
  getCivilianizedTitle,
  getServiceContext,
  getFormattedRank,
  calculateYearsOfService,
  getCoreStrengths,
  getTranslatedRoleSummary,
};
